import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from 'support/helpers/currency/currency';

import { useDocumentCurrency } from '../../helpers';
import { Summary } from '../types';

type RemittanceAdviceSummaryValues = {
  remittedAmount: number;
  correctionAmount: number;
  dueAmount: number;
};

export const useRemittanceAdviceSummary = (): Summary => {
  const { t } = useTranslation();
  const currency = useDocumentCurrency();
  const values = useRemittanceAdviceSummaryValues();

  return useMemo(
    () => ({
      content: [
        [
          {
            label: t('webedi:documentSummary.remittanceAdvice.dueAmount.label'),
            value: <span className="tabular-nums">{formatCurrency(values.dueAmount, currency)}</span>,
          },
          {
            label: t('webedi:documentSummary.remittanceAdvice.correctionAmount.label'),
            value: <span className="tabular-nums">{formatCurrency(values.correctionAmount, currency)}</span>,
          },
          {
            label: t('webedi:documentSummary.remittanceAdvice.remittedAmount.label'),
            value: <span className="tabular-nums">{formatCurrency(values.remittedAmount, currency)}</span>,
          },
        ],
      ],
    }),
    [t, values, currency],
  );
};

const useRemittanceAdviceSummaryValues = (): RemittanceAdviceSummaryValues => {
  const [remittedAmount, correctionAmount, dueAmount] = useWatch({
    name: ['info.0.remitted_amount', 'info.0.correction_amount', 'info.0.due_amount'],
  });

  return useMemo(
    () => ({
      remittedAmount: remittedAmount ?? 0,
      correctionAmount: correctionAmount ?? 0,
      dueAmount: dueAmount ?? 0,
    }),
    [remittedAmount, correctionAmount, dueAmount],
  );
};
